import { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { clientService, clientAuthService, type ClientPortalData } from '../../services'
import { ProgressBar } from '../../components/assignment'
import { LoadingScreen } from '../../components/common'
import { CreateAccountModal } from '../../components/client'

interface OverallStats {
  totalPrograms: number
  completedPrograms: number
  totalExercises: number
  completedExercises: number
  progressPercent: number
}

export function ClientPortalPage() {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [data, setData] = useState<ClientPortalData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }
    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu])

  const handleSignOut = async () => {
    setShowUserMenu(false)
    try {
      await clientAuthService.signOut()
      navigate('/client/login')
    } catch (err) {
      console.error('Sign out failed:', err)
    }
  }

  useEffect(() => {
    if (token) {
      loadPortalData()
    }
  }, [token])

  // Calculate stats - must be called before any early returns (Rules of Hooks)
  const stats: OverallStats = useMemo(() => {
    if (!data) {
      return {
        totalPrograms: 0,
        completedPrograms: 0,
        totalExercises: 0,
        completedExercises: 0,
        progressPercent: 0,
      }
    }

    const activeAssignments = data.assignments.filter((a) => a.status === 'active')
    const completedAssignments = data.assignments.filter((a) => a.status === 'completed')
    const allAssignments = [...activeAssignments, ...completedAssignments]
    const totalPrograms = allAssignments.length
    const completedPrograms = completedAssignments.length

    let totalExercises = 0
    let completedExercises = 0

    allAssignments.forEach((a) => {
      if (a.progress_summary) {
        totalExercises += a.progress_summary.total_items
        completedExercises += a.progress_summary.completed_items
      }
    })

    const progressPercent = totalExercises > 0
      ? Math.round((completedExercises / totalExercises) * 100)
      : 0

    return {
      totalPrograms,
      completedPrograms,
      totalExercises,
      completedExercises,
      progressPercent,
    }
  }, [data])

  const loadPortalData = async () => {
    if (!token) return
    setIsLoading(true)
    setError(null)
    try {
      // Validate client access token and fetch their assignments in one call
      const portalData = await clientService.validateClientAccessToken(token)
      if (!portalData) {
        setError('Invalid or expired access link')
        return
      }

      setData(portalData)
    } catch (err) {
      console.error('Failed to load portal:', err)
      setError('Failed to load your programs. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return <LoadingScreen message="Loading your programs..." />
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">
            Access Unavailable
          </h1>
          <p className="text-gray-600">
            {error || 'This link may have expired or is invalid.'}
          </p>
          <p className="text-sm text-gray-500 mt-4">
            Please contact your provider for a new access link.
          </p>
        </div>
      </div>
    )
  }

  // Filter assignments for rendering (data is guaranteed to exist here)
  const activeAssignments = data.assignments.filter((a) => a.status === 'active')
  const completedAssignments = data.assignments.filter(
    (a) => a.status === 'completed'
  )

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      {/* Fixed Header - Mobile App Style */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 h-14 sm:h-16 flex items-center justify-between">
          {/* Logo/Brand */}
          <div className="flex items-center gap-2">
            <img
              src="/logo-icon.png"
              alt="Exercise Toolbox"
              className="w-8 h-8 object-contain"
            />
            <span className="font-semibold text-gray-900 hidden sm:block">
              Exercise Toolbox
            </span>
          </div>

          {/* User Menu - Always Top Right */}
          <div className="relative" ref={userMenuRef}>
            {data.client.user_id ? (
              /* Logged-in user with dropdown */
              <>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <div className="w-9 h-9 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center shadow-sm">
                    <span className="text-sm font-semibold text-white">
                      {data.client.full_name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                  <svg
                    className={`w-4 h-4 text-gray-500 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Dropdown */}
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="font-semibold text-gray-900">{data.client.full_name}</p>
                      <p className="text-sm text-gray-500 truncate">{data.client.email}</p>
                    </div>
                    <div className="py-1">
                      <button
                        onClick={handleSignOut}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Create Account button */
              <button
                onClick={() => setShowCreateAccountModal(true)}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="hidden sm:inline">Create Account</span>
                <span className="sm:hidden">Sign Up</span>
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 sm:py-8">
        {/* Welcome Section */}
        <section className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Welcome back, {data.client.full_name?.split(' ')[0]}!
          </h1>
          <p className="text-gray-600 mt-1">
            Here are your assigned exercise programs
          </p>
        </section>

        {/* Progress Overview - Only show if has programs */}
        {data.assignments.length > 0 && (
          <section className="mb-6 sm:mb-8">
            <div className="bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 shadow-sm">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                Your Progress
              </h2>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-5">
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-blue-600">
                    {stats.totalPrograms}
                  </div>
                  <div className="text-xs sm:text-sm text-blue-700 mt-1">Programs</div>
                </div>
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-green-600">
                    {stats.completedPrograms}
                  </div>
                  <div className="text-xs sm:text-sm text-green-700 mt-1">Completed</div>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-purple-600">
                    {stats.completedExercises}
                  </div>
                  <div className="text-xs sm:text-sm text-purple-700 mt-1">Exercises Done</div>
                </div>
                <div className="bg-orange-50 rounded-xl p-4 text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-orange-600">
                    {stats.progressPercent}%
                  </div>
                  <div className="text-xs sm:text-sm text-orange-700 mt-1">Progress</div>
                </div>
              </div>

              {/* Progress Bar */}
              <ProgressBar
                completed={stats.completedExercises}
                total={stats.totalExercises}
                size="lg"
                color={stats.progressPercent === 100 ? 'green' : 'blue'}
              />

              {stats.progressPercent === 100 && (
                <div className="mt-4 flex items-center justify-center gap-2 text-green-600 bg-green-50 rounded-lg py-3">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium">All programs completed!</span>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Active Programs */}
        {activeAssignments.length > 0 && (
          <section className="mb-6 sm:mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              Active Programs
            </h2>
            <div className="space-y-4">
              {activeAssignments.map((assignment) => (
                <Link
                  key={assignment.id}
                  to={`/c/${token}/program/${assignment.id}`}
                  className="block bg-white rounded-2xl border border-gray-200 p-5 sm:p-6 hover:shadow-md hover:border-gray-300 transition-all active:scale-[0.99]"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 text-lg">
                        {assignment.program?.title || 'Exercise Program'}
                      </h3>
                      {assignment.program?.description && (
                        <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                          {assignment.program.description}
                        </p>
                      )}
                      {assignment.notes && (
                        <div className="flex items-start gap-2 mt-3 p-3 bg-blue-50 rounded-lg">
                          <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-sm text-blue-700">{assignment.notes}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {assignment.progress_summary && (
                    <div className="mt-4">
                      <ProgressBar
                        completed={assignment.progress_summary.completed_items}
                        total={assignment.progress_summary.total_items}
                        showLabel
                      />
                    </div>
                  )}
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Completed Programs */}
        {completedAssignments.length > 0 && (
          <section className="mb-6 sm:mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Completed Programs
            </h2>
            <div className="space-y-3">
              {completedAssignments.map((assignment) => (
                <Link
                  key={assignment.id}
                  to={`/c/${token}/program/${assignment.id}`}
                  className="block bg-white/70 rounded-xl border border-gray-200 p-4 hover:bg-white transition-colors"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-medium text-gray-900 truncate">
                          {assignment.program?.title || 'Exercise Program'}
                        </h3>
                      </div>
                    </div>
                    <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Empty State */}
        {data.assignments.length === 0 && (
          <section className="py-8">
            <div className="bg-white rounded-2xl border border-gray-200 p-8 sm:p-12 text-center shadow-sm">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-10 h-10 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
                No Programs Yet
              </h2>
              <p className="text-gray-600 max-w-sm mx-auto">
                Your provider hasn't assigned any exercise programs to you yet.
                Check back soon!
              </p>
              <div className="mt-6 pt-6 border-t border-gray-100">
                <p className="text-sm text-gray-500">
                  Questions? Contact your provider for more information.
                </p>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white mt-auto">
        <div className="max-w-4xl mx-auto px-4 py-4 sm:py-6 text-center">
          <p className="text-sm text-gray-500">
            Exercise Toolbox
          </p>
        </div>
      </footer>

      {/* Create Account Modal */}
      <CreateAccountModal
        isOpen={showCreateAccountModal}
        onClose={() => setShowCreateAccountModal(false)}
        clientId={data.client.id}
        clientEmail={data.client.email || ''}
        clientName={data.client.full_name || ''}
        onSuccess={() => {
          // Reload portal data to update the user_id status
          loadPortalData()
        }}
      />
    </div>
  )
}
